import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ChevronRight, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getTradingLimitsIntradayAlgoTradingLimitsGet as getTradingLimits } from "@/client-admin/services.gen";
import { LimitCheckResult, TradingLimitsResponse } from "@/client-admin/types.gen";
import { cn, getErrorDetails } from "@/lib/utils";
import { BreachList } from "./BreachList";
import {
  BREACH_UNITS,
  checkFields,
  checkLabel,
  checkUnit,
  formatAmsterdamDateTime,
  formatCheckNumber,
  formatPeriod,
  mergeConsecutivePeriods,
  sameTradingLimit,
} from "./tradingLimits";

interface TradingLimitsStripProps {
  deliveryDate: string;
  /** Hidden when undefined, e.g. while the Limits tab is already open. */
  onViewLimits?: () => void;
}

/** How far the live signals (Algo 1/2) of one delivery date are into each limit. */
export function TradingLimitsStrip({ deliveryDate, onViewLimits }: TradingLimitsStripProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["intraday-algo-trading-limits", deliveryDate],
    queryFn: () => getTradingLimits({ deliveryDate }),
    refetchInterval: 60_000,
    // Signals are enabled on the run page; coming back must show the new usage.
    refetchOnMount: "always",
  });

  return (
    <section
      aria-label={`Trading limits for ${deliveryDate}`}
      className="rounded-lg border bg-card px-3 py-2.5 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-sm font-semibold">Limits for {deliveryDate}</h3>
        {data && <StripSubtitle data={data} />}
        {onViewLimits && (
          <Button variant="link" size="sm" className="ml-auto h-auto p-0" onClick={onViewLimits}>
            View limits
            <ChevronRight className="ml-0.5 h-4 w-4" />
          </Button>
        )}
      </div>

      {isLoading && <Loader2 className="mx-auto my-3 h-5 w-5 animate-spin" />}
      {error && (
        <p className="mt-2 flex items-center gap-1 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" />
          {getErrorDetails(error)}
        </p>
      )}
      {data && <UsageMeters data={data} />}
    </section>
  );
}

function StripSubtitle({ data }: { data: TradingLimitsResponse }) {
  const overriddenAt = data.overridden_at && formatAmsterdamDateTime(data.overridden_at);
  return (
    <p className="text-xs text-muted-foreground">
      {data.overridden_by ? `Overridden by ${data.overridden_by} at ${overriddenAt}` : "Client defaults"}. Live signals
      only; slicing stops {data.effective.minutes_to_closed_id} min before delivery.
    </p>
  );
}

function UsageMeters({ data }: { data: TradingLimitsResponse }) {
  const [openCheck, setOpenCheck] = useState<LimitCheckResult["limit_name"] | null>(null);
  const results = data.usage.results;
  const openResult = results.find((result) => result.limit_name === openCheck && result.breaches.length > 0);
  if (results.length === 0) {
    return (
      <p className="mt-2 text-sm text-muted-foreground">
        No live signals for {data.delivery_date} yet. Usage shows once signals are set to Algo 1.
      </p>
    );
  }
  const missingPeriods = mergeConsecutivePeriods(
    data.usage.missing_day_ahead_prices.map((ptu) => ({ start: ptu.start_time, end: ptu.end_time })),
  );
  return (
    <>
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
        {results.map((result) => (
          <UsageMeter
            key={result.limit_name}
            result={result}
            open={openResult?.limit_name === result.limit_name}
            onToggle={() => setOpenCheck(openResult?.limit_name === result.limit_name ? null : result.limit_name)}
            overridden={checkFields(result.limit_name).some(
              (field) => !sameTradingLimit(data.effective[field], data.default[field]),
            )}
          />
        ))}
      </ul>
      {openResult && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50/50 p-2" id="trading-limit-breaches">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-red-700">
              {checkLabel(openResult.limit_name)}: {openResult.breaches.length}{" "}
              {openResult.breaches.length === 1
                ? BREACH_UNITS[openResult.limit_name].one
                : BREACH_UNITS[openResult.limit_name].counted}{" "}
              over the limit
            </p>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setOpenCheck(null)}
              aria-label="Close breach list"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <BreachList result={openResult} />
        </div>
      )}
      {missingPeriods.length > 0 && (
        <p className="mt-2 text-xs text-red-700">
          Day Ahead price missing: {missingPeriods.map((period) => formatPeriod(period.start, period.end)).join(", ")}
        </p>
      )}
    </>
  );
}

type MeterTone = "ok" | "near" | "over" | "muted";

const TONES: Record<MeterTone, { bar: string; text: string }> = {
  ok: { bar: "bg-green-600", text: "text-muted-foreground" },
  near: { bar: "bg-amber-500", text: "text-amber-700" },
  over: { bar: "bg-red-600", text: "text-red-700" },
  muted: { bar: "bg-transparent", text: "text-muted-foreground" },
};

/** Share of the limit used by the worst PTU, hour or day; a 0 limit is full as soon as anything is measured. */
const usedShare = (actual: number, limit: number): number => {
  if (limit > 0) return actual / limit;
  return actual > 0 ? Infinity : 0;
};

const shareTone = (share: number): MeterTone => {
  if (share > 1) return "over";
  if (share >= 0.8) return "near";
  return "ok";
};

interface UsageMeterProps {
  result: LimitCheckResult;
  overridden: boolean;
  open: boolean;
  onToggle: () => void;
}

function UsageMeter({ result, overridden, open, onToggle }: UsageMeterProps) {
  const label = checkLabel(result.limit_name);
  const worst = result.worst;
  const expandable = result.breaches.length > 0;

  let tone: MeterTone = "muted";
  let share = 0;
  let detail = "";
  if (!result.enabled) {
    detail = "Off";
  } else if (!worst) {
    tone = "over";
    detail = "Not measured";
  } else {
    share = usedShare(Number(worst.actual), Number(worst.limit));
    tone = shareTone(share);
    const percent = Number.isFinite(share) ? `${Math.round(share * 100)}%` : "over";
    const breaches = result.breaches.length;
    detail = breaches > 1 ? `${percent}, ${breaches} ${BREACH_UNITS[result.limit_name].counted} over` : percent;
  }

  const content = (
    <>
      <div className="flex items-center gap-1.5">
        {overridden && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" title="Overridden for this delivery date" />
        )}
        <span className="truncate text-xs text-muted-foreground" title={label}>
          {label}
        </span>
      </div>
      <p className="mt-0.5 truncate text-sm tabular-nums">
        {worst ? (
          <>
            <span className="font-medium">{formatCheckNumber(result.limit_name, worst.actual)}</span>
            <span className="text-muted-foreground">
              {" "}
              / {formatCheckNumber(result.limit_name, worst.limit)} {checkUnit(result.limit_name)}
            </span>
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </p>
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Number.isFinite(share) ? Math.round(share * 100) : 100}
      >
        <div className={cn("h-full rounded-full", TONES[tone].bar)} style={{ width: `${Math.min(share, 1) * 100}%` }} />
      </div>
      <p className={cn("mt-0.5 flex items-center gap-0.5 truncate text-xs tabular-nums", TONES[tone].text)}>
        {detail}
        {expandable && <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />}
      </p>
    </>
  );

  // Checks with breaches open their breach list, with the prices behind each one.
  return (
    <li className="min-w-0">
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls="trading-limit-breaches"
          title="Show every breach"
          className={cn(
            "-m-1.5 w-[calc(100%+0.75rem)] rounded-md p-1.5 text-left transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            open && "bg-red-50",
          )}
        >
          {content}
        </button>
      ) : (
        content
      )}
    </li>
  );
}
